import { CommonTrackingConfig } from '../../../modules/utils/trackingHelper';

export type WalletAwardModalLayout = 'howToEarn' | 'awardTask';

/** Award status constants used in onAwardTileClick handlers */
export const WALLET_AWARD_STATUS = {
  AVAILABLE: 'available',
  APPLIED: 'applied',
  REQUEST_PENDING: 'requestPending',
  APPROVED: 'approved',
} as const;

export type WalletAwardStatus = (typeof WALLET_AWARD_STATUS)[keyof typeof WALLET_AWARD_STATUS];

/** Runtime modal IDs used by openModal / activeModal state. */
export const WALLET_AWARD_MODAL_ID = {
  FNA_HOW_TO_EARN: 'fna-how-to-earn',
  NUA_HOW_TO_EARN: 'nua-how-to-earn',
  NUA_AVAILABLE: 'nua-available',
  FNA_APPLIED: 'fna-applied',
  NUA_PENDING: 'nua-pending',
  FNA_AVAILABLE: 'fna-available',
  NUA_APPROVED: 'nua-approved',
} as const;

export type WalletAwardModalId = (typeof WALLET_AWARD_MODAL_ID)[keyof typeof WALLET_AWARD_MODAL_ID];

/** AEM instances.* keys under the modals slice. */
export const WALLET_AWARD_MODAL_INSTANCE = {
  FNA_HOW_TO_EARN: 'fnaHowToEarn',
  NUA_HOW_TO_EARN: 'nuaHowToEarn',
  NUA_AVAILABLE: 'nuaAvailable',
  FNA_APPLIED: 'fnaApplied',
  NUA_PENDING: 'nuaPending',
  FNA_AVAILABLE: 'fna-available',
  NUA_APPROVED: 'nuaApproved',
} as const;

export type WalletAwardModalInstanceKey =
  (typeof WALLET_AWARD_MODAL_INSTANCE)[keyof typeof WALLET_AWARD_MODAL_INSTANCE];

export const WALLET_AWARD_MODAL_ID_TO_INSTANCE: Record<WalletAwardModalId, WalletAwardModalInstanceKey> = {
  [WALLET_AWARD_MODAL_ID.FNA_HOW_TO_EARN]: WALLET_AWARD_MODAL_INSTANCE.FNA_HOW_TO_EARN,
  [WALLET_AWARD_MODAL_ID.NUA_HOW_TO_EARN]: WALLET_AWARD_MODAL_INSTANCE.NUA_HOW_TO_EARN,
  [WALLET_AWARD_MODAL_ID.NUA_AVAILABLE]: WALLET_AWARD_MODAL_INSTANCE.NUA_AVAILABLE,
  [WALLET_AWARD_MODAL_ID.FNA_APPLIED]: WALLET_AWARD_MODAL_INSTANCE.FNA_APPLIED,
  [WALLET_AWARD_MODAL_ID.NUA_PENDING]: WALLET_AWARD_MODAL_INSTANCE.NUA_PENDING,
  [WALLET_AWARD_MODAL_ID.FNA_AVAILABLE]: WALLET_AWARD_MODAL_INSTANCE.FNA_AVAILABLE,
  [WALLET_AWARD_MODAL_ID.NUA_APPROVED]: WALLET_AWARD_MODAL_INSTANCE.NUA_APPROVED,
};

// ── AEM Lookup Mapper (QA AEM contract) ──────────────────────────────────────

export type AemModalSlice = 'howtoEarn' | 'details';
export type AemAwardKey = 'fna' | 'nua';
export const AEM_DETAIL_STATE_KEY = {
  DEFAULT: 'default',
  APPLIED: 'applied',
  PENDING: 'pending',
  APPROVED: 'approved',
} as const;

export type AemDetailStateKey = (typeof AEM_DETAIL_STATE_KEY)[keyof typeof AEM_DETAIL_STATE_KEY];

export const WALLET_AWARD_STATUS_VARIANT = {
  SUCCESS: 'success',
  NEUTRAL: 'neutral',
  INFO: 'info',
} as const;

export type WalletAwardStatusVariant = (typeof WALLET_AWARD_STATUS_VARIANT)[keyof typeof WALLET_AWARD_STATUS_VARIANT];

export type WalletAwardStatusPresentation = {
  label?: string;
  variant: WalletAwardStatusVariant;
  iconClass?: string;
};

/** Statuses that render the reservation card footer text list item. */
export const RESERVATION_FOOTER_STATUSES: readonly WalletAwardStatus[] = [
  WALLET_AWARD_STATUS.APPLIED,
  WALLET_AWARD_STATUS.REQUEST_PENDING,
  WALLET_AWARD_STATUS.APPROVED,
];

export const isReservationFooterStatus = (status?: string): status is WalletAwardStatus =>
  Boolean(status && RESERVATION_FOOTER_STATUSES.includes(status as WalletAwardStatus));

/**
 * AEM modal source descriptor - maps runtime modal ID to QA AEM contract path.
 */
export interface AemModalSource {
  slice: AemModalSlice;
  awardKey: AemAwardKey;
  stateKey?: AemDetailStateKey;
}

/**
 * Maps runtime modal IDs to AEM contract paths.
 * Used by resolver to locate modal content in the QA AEM model.
 */
export const resolveAemModalSource = (modalId: WalletAwardModalId): AemModalSource => {
  switch (modalId) {
    case WALLET_AWARD_MODAL_ID.FNA_HOW_TO_EARN:
      return { slice: 'howtoEarn', awardKey: 'fna' };
    case WALLET_AWARD_MODAL_ID.NUA_HOW_TO_EARN:
      return { slice: 'howtoEarn', awardKey: 'nua' };
    case WALLET_AWARD_MODAL_ID.NUA_AVAILABLE:
      return { slice: 'details', awardKey: 'nua', stateKey: AEM_DETAIL_STATE_KEY.DEFAULT };
    case WALLET_AWARD_MODAL_ID.FNA_APPLIED:
      return { slice: 'details', awardKey: 'fna', stateKey: AEM_DETAIL_STATE_KEY.APPLIED };
    case WALLET_AWARD_MODAL_ID.FNA_AVAILABLE:
      return { slice: 'details', awardKey: 'fna', stateKey: AEM_DETAIL_STATE_KEY.DEFAULT };
    case WALLET_AWARD_MODAL_ID.NUA_PENDING:
      return { slice: 'details', awardKey: 'nua', stateKey: AEM_DETAIL_STATE_KEY.PENDING };
    case WALLET_AWARD_MODAL_ID.NUA_APPROVED:
      return { slice: 'details', awardKey: 'nua', stateKey: AEM_DETAIL_STATE_KEY.APPROVED };
    default:
      return { slice: 'howtoEarn', awardKey: 'fna' };
  }
};

export interface WalletAwardInstructionStep {
  icon?: string;
  title?: string;
  body?: string;
}

export interface WalletAwardsModalsShared {
  closeLabel?: string;
  learnMoreCtaLabel?: string;
  searchHotelsCtaLabel?: string;
  searchHotelsCtaLink?: string;
  searchHotelsCtaOpenInANewTab?: boolean;
  expirationHeading?: string;
  expirationCountSingularLabel?: string;
  expirationCountPluralLabel?: string;
  expiringSoonDays?: number;
  tracking?: CommonTrackingConfig[];
}

export interface WalletAwardsModalsLayouts {
  howToEarn?: {
    howToEarnSectionHeading?: string;
  };
  awardTask?: {
    instructionSteps?: WalletAwardInstructionStep[];
    disclaimer?: string;
  };
}

export interface WalletAwardModalInstance {
  layout?: WalletAwardModalLayout;
  modalTitle?: string;
  contentTitle?: string;
  contentDescription?: string;
  howToEarnBody?: string;
  awardCountSingularLabel?: string;
  awardCountPluralLabel?: string;
  tipsToggleLabel?: string;
  tipsBody?: string;
  instructionSteps?: WalletAwardInstructionStep[];
  disclaimer?: string;
  reservationDetailsHeading?: string;
}

export interface WalletAwardsModalsSlice {
  shared?: WalletAwardsModalsShared;
  layouts?: WalletAwardsModalsLayouts;
  instances?: Partial<Record<WalletAwardModalInstanceKey, WalletAwardModalInstance>>;
}

export interface ResolvedWalletModalContent {
  layout?: WalletAwardModalLayout;
  modalTitle?: string;
  contentTitle?: string;
  contentDescription?: string;
  howToEarnSectionHeading?: string;
  howToEarnBody?: string;
  closeLabel?: string;
  primaryCtaLabel?: string;
  primaryCtaLink?: string;
  primaryCtaOpenInANewTab?: boolean;
  searchHotelsCtaLabel?: string;
  searchHotelsCtaLink?: string;
  searchHotelsCtaOpenInANewTab?: boolean;
  heroIconClass?: string;
  heroImageSrc?: string;
  heroImageAltText?: string;
  expiresLabel?: string;
  expiresSingularLabel?: string;
  expirationHeading?: string;
  awardExpirationLabel?: string;
  awardStatustitle?: string;
  awardStatusDescription?: string;
  confirmationNumberLabel?: string;
  checkInLabel?: string;
  checkOutLabel?: string;
  expirationCountSingularLabel?: string;
  expirationCountPluralLabel?: string;
  expiringSoonDays?: number;
  awardCountSingularLabel?: string;
  awardCountPluralLabel?: string;
  tipsToggleLabel?: string;
  tipsBody?: string;
  tipsList?: WalletAwardInstructionStep[];
  instructionSteps?: WalletAwardInstructionStep[];
  disclaimer?: string;
  reservationDetailsHeading?: string;
  tracking?: CommonTrackingConfig[];
  tipsAccordionTracking?: {
    toggleOpenValue: string;
    toggleClosedValue: string;
  };
}
